import {Menu} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import type {MRT_ColumnFiltersState, MRT_SortingState} from 'mantine-react-table';
import React from 'react';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {useAppSelector} from 'src/redux/hooks';
import {useGetDocumentHistoryListV2Query} from 'src/redux/queries/document';
import {getUrlWithEncodingAndAuth} from 'src/shared/utils/urlHelpers';
import {ReviewRoundDetail} from 'src/pages/Modules/DocumentManagement/ViewDocument/components/ReviewRoundDetail';
import {useDocumentReviewRoundsByVersion} from 'src/pages/Modules/DocumentManagement/ViewDocument/hooks/useDocumentReviewRoundsByVersion';
import {useDocumentVersionsColumns} from './hooks/useDocumentVersionsColumns';

export interface DocumentVersionsTableProps {
  identifier: string;
  documentId: number;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
  actionBarPreNode?: React.ReactNode;
  actionBarPostNode?: React.ReactNode;
}

export const DocumentVersionsTable: React.FC<DocumentVersionsTableProps> = ({
  identifier,
  documentId,
  defaultSorting,
  defaultColumnFilters,
  actionBarPreNode,
  actionBarPostNode,
}) => {
  const {accessToken} = useAppSelector(state => state.auth);
  const {
    hasFeature: hasReviewsFeature,
    isLoading: isReviewRoundsLoading,
    roundsByVersion,
  } = useDocumentReviewRoundsByVersion(documentId);
  const columns = useDocumentVersionsColumns({
    showStatusColumn: hasReviewsFeature,
  });

  const handleDownload = (attachmentUrl: string | undefined) => {
    if (!attachmentUrl) return;
    const url = JSON.parse(attachmentUrl)[0].value;
    window.open(getUrlWithEncodingAndAuth(url, accessToken), '_blank');
  };

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'version', desc: true}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetDocumentHistoryListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        documentId: documentId,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      enableRowActions: true,
      columns: columns,
      data: rows,
      renderDetailPanel:
        hasReviewsFeature && !isReviewRoundsLoading
          ? ({row}) => (
              <Box px="xl" py="md">
                <ReviewRoundDetail
                  rounds={roundsByVersion.get(row.original.version) ?? []}
                />
              </Box>
            )
          : undefined,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => handleDownload(row.original.attachmentURL)}>
            Download
          </Menu.Item>
        </>
      ),
      renderTopToolbarCustomActions:
        actionBarPreNode || actionBarPostNode
          ? () => (
              <Group justify="space-between" gap={3}>
                {actionBarPreNode}
                {actionBarPreNode && actionBarPostNode && (
                  <Divider orientation="vertical" />
                )}
                {actionBarPostNode}
              </Group>
            )
          : undefined,
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
